import React, {useCallback, useEffect, useMemo, useRef, useState} from "react";
import {Alert, Badge, Button, Form, Modal, ProgressBar, Spinner, Table} from "react-bootstrap";
import {toast} from "react-toastify";
import {
    adminFetchEmailCampaignRecipientsApi,
    adminFetchEmailCampaignsApi,
    adminSaveEmailCampaignApi,
    adminSendEmailCampaignOneApi,
    adminSendWeeklyDigestTestApi,
    adminUploadCampaignImageApi,
} from "../../http/userAPI";

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

const statusVariant = (status) => {
    if (status === "sent") return "success";
    if (status === "sending") return "primary";
    if (status === "skipped") return "warning";
    if (status === "error") return "danger";
    return "secondary";
};

const makeRow = (user) => ({
    id: user.id,
    username: user.username || "-",
    email: user.email || "-",
    plan: user.tariff_plan || "-",
    status: user.status || "queued",
    note: user.note || "Waiting",
});

const richTextColors = [
    {label: "Default", value: "#333333"},
    {label: "Green", value: "#198754"},
    {label: "Red", value: "#dc3545"},
    {label: "Muted", value: "#6c757d"},
];

const insertAroundSelection = ({textareaRef, value, onChange, openTag, closeTag, fallbackText = ""}) => {
    const textarea = textareaRef.current;
    const start = textarea?.selectionStart ?? value.length;
    const end = textarea?.selectionEnd ?? value.length;
    const selected = value.slice(start, end) || fallbackText;
    const nextValue = `${value.slice(0, start)}${openTag}${selected}${closeTag}${value.slice(end)}`;
    onChange(nextValue);

    requestAnimationFrame(() => {
        if (!textarea) return;
        textarea.focus();
        const nextStart = start + openTag.length;
        const nextEnd = nextStart + selected.length;
        textarea.setSelectionRange(nextStart, nextEnd);
    });
};

const RichTextToolbar = ({textareaRef, value, onChange, disabled}) => {
    const applyTag = (openTag, closeTag, fallbackText = "text") => {
        insertAroundSelection({textareaRef, value, onChange, openTag, closeTag, fallbackText});
    };

    const applyLink = () => {
        const href = window.prompt("Link URL", "https://");
        if (!href) return;
        applyTag(`<a href="${href.replace(/"/g, "")}">`, "</a>", "link text");
    };

    return (
        <div className="d-flex flex-wrap align-items-center gap-1 mb-1">
            <Button
                variant="outline-secondary"
                size="sm"
                disabled={disabled}
                className="fw-bold px-2 py-0"
                title="Bold"
                onClick={() => applyTag("<strong>", "</strong>", "bold text")}
            >
                B
            </Button>
            <Button
                variant="outline-secondary"
                size="sm"
                disabled={disabled}
                className="fst-italic px-2 py-0"
                title="Italic"
                onClick={() => applyTag("<em>", "</em>", "italic text")}
            >
                I
            </Button>
            <Button
                variant="outline-success"
                size="sm"
                disabled={disabled}
                className="px-2 py-0"
                title="Subtitle"
                onClick={() => applyTag("<h3>", "</h3>", "Subtitle")}
            >
                H
            </Button>
            <Form.Select
                size="sm"
                disabled={disabled}
                value=""
                title="Text color"
                style={{width: 96, height: 28, paddingTop: 1, paddingBottom: 1}}
                onChange={(e) => {
                    if (!e.target.value) return;
                    applyTag(`<span style="color:${e.target.value};">`, "</span>", "colored text");
                }}
            >
                <option value="">Color</option>
                {richTextColors.map((color) => (
                    <option key={color.value} value={color.value}>{color.label}</option>
                ))}
            </Form.Select>
            <Button
                variant="outline-primary"
                size="sm"
                disabled={disabled}
                className="px-2 py-0"
                title="Link"
                onClick={applyLink}
            >
                Link
            </Button>
        </div>
    );
};

const AdminEmailMailerModal = () => {
    const [show, setShow] = useState(false);
    const [campaignKey, setCampaignKey] = useState("");
    const [campaignMode, setCampaignMode] = useState("digest");
    const [campaigns, setCampaigns] = useState([]);
    const [senderName, setSenderName] = useState("Virtalibry Support");
    const [subject, setSubject] = useState("");
    const [introBody, setIntroBody] = useState("");
    const [body, setBody] = useState("");
    const [linkUrl, setLinkUrl] = useState("");
    const [buttonText, setButtonText] = useState("Open link");
    const [imageFile, setImageFile] = useState(null);
    const [imagePreview, setImagePreview] = useState("");
    const [uploadedImageUrl, setUploadedImageUrl] = useState("");
    const [testUserId, setTestUserId] = useState("");
    const [limit, setLimit] = useState(25);
    const [tariffPlan, setTariffPlan] = useState("ALL");
    const [delayMs, setDelayMs] = useState(1200);
    const [batchSize, setBatchSize] = useState(1);
    const [rows, setRows] = useState([]);
    const [rowsMode, setRowsMode] = useState("digest");
    const [loadingRecipients, setLoadingRecipients] = useState(false);
    const [loadingCampaigns, setLoadingCampaigns] = useState(false);
    const [savingCampaign, setSavingCampaign] = useState(false);
    const [running, setRunning] = useState(false);
    const [runningMode, setRunningMode] = useState("");
    const [progress, setProgress] = useState({done: 0, total: 0});
    const stopRef = useRef(false);
    const introBodyRef = useRef(null);
    const bodyRef = useRef(null);

    const stats = useMemo(() => rows.reduce((acc, row) => {
        acc[row.status] = (acc[row.status] || 0) + 1;
        return acc;
    }, {queued: 0, sent: 0, skipped: 0, error: 0, sending: 0}), [rows]);

    const resetState = () => {
        stopRef.current = false;
        setRows([]);
        setRowsMode("digest");
        setCampaignMode("digest");
        setProgress({done: 0, total: 0});
        setUploadedImageUrl("");
    };

    const handleClose = () => {
        if (running) return;
        resetState();
        setShow(false);
    };

    const patchRow = (id, patch) => {
        setRows((prev) => prev.map((row) => (row.id === id ? {...row, ...patch} : row)));
    };

    const handleImageChange = (e) => {
        const file = e.target.files?.[0] || null;
        setImageFile(file);
        setUploadedImageUrl("");
        if (imagePreview) URL.revokeObjectURL(imagePreview);
        setImagePreview(file ? URL.createObjectURL(file) : "");
    };

    const ensureImageUploaded = async () => {
        if (uploadedImageUrl) return uploadedImageUrl;
        if (!imageFile) return "";
        const uploaded = await adminUploadCampaignImageApi(imageFile);
        setUploadedImageUrl(uploaded.url || "");
        return uploaded.url || "";
    };

    const loadCampaigns = useCallback(async () => {
        setLoadingCampaigns(true);
        try {
            const result = await adminFetchEmailCampaignsApi({limit: 50});
            setCampaigns(result.items || []);
        } catch (e) {
            toast.error(e?.response?.data?.message || e?.message || "Failed to load saved campaigns.");
        } finally {
            setLoadingCampaigns(false);
        }
    }, []);

    useEffect(() => {
        if (!show) return;
        loadCampaigns();
    }, [show, loadCampaigns]);

    const applyCampaign = (campaignKeyValue) => {
        const campaign = campaigns.find((item) => item.campaignKey === campaignKeyValue);
        if (!campaign) return;
        setCampaignKey(campaign.campaignKey || "");
        setSenderName(campaign.senderName || "Virtalibry Support");
        setSubject(campaign.subject || "");
        setIntroBody(campaign.introBody || "");
        setBody(campaign.body || "");
        setLinkUrl(campaign.linkUrl || "");
        setButtonText(campaign.buttonText || "Open link");
        setUploadedImageUrl(campaign.imageUrl || "");
        setImagePreview(campaign.imageUrl || "");
        setImageFile(null);
        setCampaignMode(campaign.mode || "digest");
        setRowsMode(campaign.mode || "digest");
        setRows([]);
        setProgress({done: 0, total: 0});
    };

    const validateCampaignKey = () => {
        if (!campaignKey.trim()) {
            toast.error("Campaign key is required for bulk sending.");
            return false;
        }
        return true;
    };

    const saveCampaign = async (mode = "digest") => {
        if (!validateCampaignKey()) return null;
        if (mode === "digest" && !validateContent()) return null;
        const subjectToSave = mode === "weekly" && !subject.trim()
            ? "Your Weekly Virtalibry Picks"
            : subject;
        setSavingCampaign(true);
        try {
            const imageUrl = await ensureImageUploaded();
            const result = await adminSaveEmailCampaignApi({
                campaignKey,
                mode,
                senderName,
                subject: subjectToSave,
                introBody,
                body,
                imageUrl,
                linkUrl,
                buttonText,
            });
            if (result.campaign?.campaignKey) setCampaignKey(result.campaign.campaignKey);
            if (result.campaign?.mode) setCampaignMode(result.campaign.mode);
            await loadCampaigns();
            return result.campaign;
        } catch (e) {
            toast.error(e?.response?.data?.message || e?.message || "Failed to save campaign.");
            return null;
        } finally {
            setSavingCampaign(false);
        }
    };

    const loadRecipients = async (mode = "digest") => {
        if (!validateCampaignKey()) return [];
        setCampaignMode(mode);
        setLoadingRecipients(true);
        try {
            const result = await adminFetchEmailCampaignRecipientsApi({
                limit,
                tariffPlan,
                mode,
                campaignKey,
                skipAlreadySent: true,
            });
            const nextRows = (result.items || []).map(makeRow);
            setRows(nextRows);
            setRowsMode(mode);
            setProgress({done: 0, total: nextRows.length});
            if (!nextRows.length) {
                toast.info(mode === "weekly"
                    ? "No users matched Weekly Digest recipients."
                    : "No users matched Email Digest recipients.");
            }
            return nextRows;
        } catch (e) {
            toast.error(e?.response?.data?.message || e?.message || "Failed to load recipients.");
            return [];
        } finally {
            setLoadingRecipients(false);
        }
    };

    const validateContent = () => {
        if (!subject.trim()) {
            toast.error("Subject is required.");
            return false;
        }
        if (!introBody.trim() && !body.trim()) {
            toast.error("Email text is required.");
            return false;
        }
        return true;
    };

    const sendTest = async () => {
        if (!validateContent()) return;
        if (!Number(testUserId)) {
            toast.error("Test user ID is required.");
            return;
        }
        setRunning(true);
        try {
            const imageUrl = await ensureImageUploaded();
            await adminSendEmailCampaignOneApi({
                userId: Number(testUserId),
                subject,
                senderName,
                introBody,
                body,
                imageUrl,
                linkUrl,
                buttonText,
                force: true,
                mode: "digest",
            });
            toast.success("Test email sent.");
        } catch (e) {
            toast.error(e?.response?.data?.message || e?.message || "Test email failed.");
        } finally {
            setRunning(false);
        }
    };

    const sendWeeklyDigestTest = async () => {
        if (!Number(testUserId)) {
            toast.error("Test user ID is required.");
            return;
        }
        setRunning(true);
        try {
            await adminSendWeeklyDigestTestApi({
                userId: Number(testUserId),
                subject: subject || "Your Weekly Virtalibry Picks",
                senderName,
                introBody,
                body,
                linkUrl,
                buttonText,
            });
            toast.success("Weekly digest test sent.");
        } catch (e) {
            toast.error(e?.response?.data?.message || e?.message || "Weekly digest test failed.");
        } finally {
            setRunning(false);
        }
    };

    const startCampaign = async (mode = "digest") => {
        setCampaignMode(mode);
        if (mode === "digest" && !validateContent()) return;
        if (!validateCampaignKey()) return;
        const savedCampaign = await saveCampaign(mode);
        if (!savedCampaign) return;
        const effectiveCampaignKey = savedCampaign.campaignKey || campaignKey;
        const subjectToSend = mode === "weekly" && !subject.trim()
            ? "Your Weekly Virtalibry Picks"
            : subject;
        let queue = rowsMode === mode ? rows.filter((row) => ["queued", "error"].includes(row.status)) : [];
        if (!queue.length) {
            const loadedRows = await loadRecipients(mode);
            queue = loadedRows.filter((row) => ["queued", "error"].includes(row.status));
        }
        if (!queue.length) {
            toast.info("Load recipients first.");
            return;
        }

        stopRef.current = false;
        setRunning(true);
        setRunningMode(mode);
        setProgress({done: 0, total: queue.length});

        try {
            const imageUrl = await ensureImageUploaded();
            const safeBatchSize = Math.max(1, Number(batchSize) || 1);
            for (let offset = 0; offset < queue.length; offset += safeBatchSize) {
                if (stopRef.current) break;
                const batch = queue.slice(offset, offset + safeBatchSize);
                batch.forEach((row) => patchRow(row.id, {status: "sending", note: "Sending..."}));

                const results = await Promise.all(batch.map(async (row) => {
                    try {
                        const result = await adminSendEmailCampaignOneApi({
                            userId: row.id,
                            subject: subjectToSend,
                            senderName,
                            introBody,
                            body,
                            imageUrl,
                            linkUrl,
                            buttonText,
                            campaignKey: effectiveCampaignKey,
                            mode,
                        });
                        if (result.skipped) {
                            patchRow(row.id, {status: "skipped", note: result.reason || "Skipped"});
                            return "skipped";
                        }
                        patchRow(row.id, {status: "sent", note: "Sent"});
                        return "sent";
                    } catch (e) {
                        patchRow(row.id, {
                            status: "error",
                            note: e?.response?.data?.message || e?.message || "Failed",
                        });
                        return "error";
                    }
                }));

                setProgress((prev) => ({done: prev.done + results.length, total: queue.length}));
                if (offset + safeBatchSize < queue.length && !stopRef.current) {
                    await sleep(Math.max(0, Number(delayMs) || 0));
                }
            }
        } finally {
            setRunning(false);
            setRunningMode("");
        }
    };

    const stopCampaign = () => {
        stopRef.current = true;
        setRunning(false);
        setRunningMode("");
    };

    const percent = progress.total ? Math.round((progress.done / progress.total) * 100) : 0;

    return (
        <>
            <Button variant="outline-success" size="sm" onClick={() => setShow(true)}>
                Email Campaign
            </Button>

            <Modal show={show} onHide={handleClose} size="xl" top dialogClassName="admin-email-mailer-modal">
                <Modal.Header closeButton={!running} className="py-2 px-3">
                    <Modal.Title className="h5 mb-0">Email Campaign Sender</Modal.Title>
                </Modal.Header>
                <Modal.Body className="py-3 px-3">
                    <Alert variant="light" className="border small py-2 px-3 mb-3">
                        Sends support-branded email campaigns or weekly digests. Each send button uses the matching notification preference and respects delay/batch settings.
                    </Alert>

                    <div className="row g-2 mb-2">
                        <div className="col-12 col-lg-8">
                            <div className="row g-2 mb-2">
                                <div className="col-12 col-md-3">
                                    <Form.Group>
                                        <Form.Label className="small mb-1">Campaign key</Form.Label>
                                        <Form.Control
                                            size="sm"
                                            value={campaignKey}
                                            disabled={running}
                                            onChange={(e) => setCampaignKey(e.target.value)}
                                            placeholder="july-news-2026"
                                        />
                                    </Form.Group>
                                </div>
                                <div className="col-12 col-md-3">
                                    <Form.Group>
                                        <Form.Label className="small mb-1">Saved campaigns</Form.Label>
                                        <Form.Select
                                            size="sm"
                                            value=""
                                            disabled={running || loadingCampaigns}
                                            onChange={(e) => applyCampaign(e.target.value)}
                                        >
                                            <option value="">{loadingCampaigns ? "Loading..." : "Load saved..."}</option>
                                            {campaigns.map((campaign) => (
                                                <option key={campaign.campaignKey} value={campaign.campaignKey}>
                                                    {campaign.campaignKey}
                                                </option>
                                            ))}
                                        </Form.Select>
                                    </Form.Group>
                                </div>
                                <div className="col-12 col-md-2">
                                    <Form.Group>
                                        <Form.Label className="small mb-1">Type</Form.Label>
                                        <Form.Select
                                            size="sm"
                                            value={campaignMode}
                                            disabled={running}
                                            onChange={(e) => setCampaignMode(e.target.value)}
                                        >
                                            <option value="digest">Email</option>
                                            <option value="weekly">Weekly</option>
                                        </Form.Select>
                                    </Form.Group>
                                </div>
                                <div className="col-12 col-md-4">
                                    <Form.Group>
                                        <Form.Label className="small mb-1">From name</Form.Label>
                                        <Form.Control
                                            size="sm"
                                            value={senderName}
                                            disabled={running}
                                            onChange={(e) => setSenderName(e.target.value)}
                                            placeholder="Virtalibry Support"
                                        />
                                    </Form.Group>
                                </div>
                                <div className="col-12">
                                    <Form.Group>
                                        <Form.Label className="small mb-1">Subject</Form.Label>
                                        <Form.Control
                                            size="sm"
                                            value={subject}
                                            disabled={running}
                                            onChange={(e) => setSubject(e.target.value)}
                                            placeholder="News from Virtalibry"
                                        />
                                    </Form.Group>
                                </div>
                            </div>
                            <Form.Group className="mb-2">
                                <Form.Label className="small mb-1">Text before image (optional)</Form.Label>
                                <RichTextToolbar
                                    textareaRef={introBodyRef}
                                    value={introBody}
                                    onChange={setIntroBody}
                                    disabled={running}
                                />
                                <Form.Control
                                    ref={introBodyRef}
                                    as="textarea"
                                    rows={2}
                                    size="sm"
                                    value={introBody}
                                    disabled={running}
                                    onChange={(e) => setIntroBody(e.target.value)}
                                    placeholder="Optional intro text shown after the title and before the image."
                                />
                            </Form.Group>
                            <Form.Group>
                                <Form.Label className="small mb-1">Email text after image</Form.Label>
                                <RichTextToolbar
                                    textareaRef={bodyRef}
                                    value={body}
                                    onChange={setBody}
                                    disabled={running}
                                />
                                <Form.Control
                                    ref={bodyRef}
                                    as="textarea"
                                    size="sm"
                                    rows={2}
                                    value={body}
                                    disabled={running}
                                    onChange={(e) => setBody(e.target.value)}
                                    placeholder="Write the email body. New lines become separate paragraphs."
                                />
                            </Form.Group>
                            <div className="row g-2 mt-1">
                                <div className="col-12 col-md-8">
                                    <Form.Group>
                                        <Form.Label className="small mb-1">Link at end (optional)</Form.Label>
                                        <Form.Control
                                            size="sm"
                                            value={linkUrl}
                                            disabled={running}
                                            onChange={(e) => setLinkUrl(e.target.value)}
                                            placeholder="https://virtalibry.com/..."
                                        />
                                    </Form.Group>
                                </div>
                                <div className="col-12 col-md-4">
                                    <Form.Group>
                                        <Form.Label className="small mb-1">Button text</Form.Label>
                                        <Form.Control
                                            size="sm"
                                            value={buttonText}
                                            disabled={running}
                                            maxLength={80}
                                            onChange={(e) => setButtonText(e.target.value)}
                                            placeholder="Open link"
                                        />
                                    </Form.Group>
                                </div>
                            </div>
                        </div>
                        <div className="col-12 col-lg-4">
                            <Form.Group className="mb-2">
                                <Form.Label className="small mb-1">Image (optional)</Form.Label>
                                <Form.Control size="sm" type="file" accept="image/png,image/jpeg,image/webp,image/gif" disabled={running} onChange={handleImageChange} />
                            </Form.Group>
                            <div className="border rounded d-flex align-items-center justify-content-center bg-light" style={{height: 168, overflow: "hidden"}}>
                                {imagePreview ? (
                                    <img src={imagePreview} alt="" style={{maxWidth: "100%", maxHeight: "100%", objectFit: "contain"}} />
                                ) : (
                                    <small className="text-muted">No campaign image</small>
                                )}
                            </div>
                        </div>
                    </div>

                    <div className="d-flex flex-wrap align-items-end gap-2 mb-2 small">
                        <Form.Group style={{width: 50}}>
                            <Form.Label className="small mb-1">Test UID</Form.Label>
                            <Form.Control size="sm" type="number" min={1} value={testUserId} disabled={running} onChange={(e) => setTestUserId(e.target.value)} />
                        </Form.Group>
                        <Button variant="outline-success" size="sm" disabled={running} onClick={sendTest}>
                            {running ? <Spinner size="sm" /> : "Send"}
                        </Button>
                        <Button variant="outline-primary" size="sm" disabled={running} onClick={sendWeeklyDigestTest}>
                            WD Send
                        </Button>
                        <Form.Group style={{width: 70}}>
                            <Form.Label className="small mb-1">Users</Form.Label>
                            <Form.Control size="sm" type="number" min={1} max={1000} value={limit} disabled={running} onChange={(e) => setLimit(Number(e.target.value) || 25)} />
                        </Form.Group>
                        <Form.Group style={{width: 90}}>
                            <Form.Label className="small mb-1">Plan</Form.Label>
                            <Form.Select size="sm" value={tariffPlan} disabled={running} onChange={(e) => setTariffPlan(e.target.value)}>
                                <option value="ALL">All</option>
                                <option value="FREE">FREE</option>
                                <option value="ESSENTIAL">ESSENTIAL</option>
                                <option value="PREMIUM">PREMIUM</option>
                                <option value="MAXIMUM">MAXIMUM</option>
                            </Form.Select>
                        </Form.Group>
                        <Form.Group style={{width: 70}}>
                            <Form.Label className="small mb-1">Delay, ms</Form.Label>
                            <Form.Control size="sm" type="number" min={0} value={delayMs} disabled={running} onChange={(e) => setDelayMs(Number(e.target.value) || 0)} />
                        </Form.Group>
                        <Form.Group style={{width: 50}}>
                            <Form.Label className="small mb-1">Batch</Form.Label>
                            <Form.Control size="sm" type="number" min={1} max={25} value={batchSize} disabled={running} onChange={(e) => setBatchSize(Number(e.target.value) || 1)} />
                        </Form.Group>
                        <Button variant="outline-secondary" size="sm" disabled={running || loadingRecipients} onClick={() => loadRecipients("digest")}>
                            {loadingRecipients ? <Spinner size="sm" /> : "Load E Users"}
                        </Button>
                        <Button variant="outline-primary" size="sm" disabled={running || loadingRecipients} onClick={() => loadRecipients("weekly")}>
                            {loadingRecipients ? <Spinner size="sm" /> : "Load W Users"}
                        </Button>
                        <Button variant="outline-dark" size="sm" disabled={running || savingCampaign} onClick={() => saveCampaign(campaignMode)}>
                            {savingCampaign ? <Spinner size="sm" /> : "Save Camp"}
                        </Button>
                        <Button variant="success" size="sm" disabled={running || loadingRecipients || savingCampaign} onClick={() => startCampaign("digest")}>
                            {running && runningMode === "digest" ? <Spinner size="sm" /> : "Send Email"}
                        </Button>
                        <Button variant="primary" size="sm" disabled={running || loadingRecipients || savingCampaign} onClick={() => startCampaign("weekly")}>
                            {running && runningMode === "weekly" ? <Spinner size="sm" /> : "Send W Digest"}
                        </Button>
                        {running && <Button variant="outline-danger" size="sm" onClick={stopCampaign}>Stop</Button>}
                    </div>

                    <ProgressBar now={percent} label={progress.total ? `${progress.done}/${progress.total}` : ""} className="mb-2" style={{height: 18}} />

                    <div className="d-flex flex-wrap gap-2 mb-2 small">
                        <Badge bg="secondary">Queued: {stats.queued || 0}</Badge>
                        <Badge bg="primary">Sending: {stats.sending || 0}</Badge>
                        <Badge bg="success">Sent: {stats.sent || 0}</Badge>
                        <Badge bg="warning" text="dark">Skipped: {stats.skipped || 0}</Badge>
                        <Badge bg="danger">Errors: {stats.error || 0}</Badge>
                    </div>

                    <div style={{maxHeight: 280, overflowY: "auto"}}>
                        <Table striped bordered hover size="sm" className="small align-middle mb-0">
                            <thead>
                            <tr className="small text-muted">
                                <th>UID</th>
                                <th>User</th>
                                <th>Email</th>
                                <th>Plan</th>
                                <th>Status</th>
                                <th>Note</th>
                            </tr>
                            </thead>
                            <tbody>
                            {rows.length ? rows.map((row) => (
                                <tr key={row.id} className="small text-muted">
                                    <td>{row.id}</td>
                                    <td>{row.username}</td>
                                    <td>{row.email}</td>
                                    <td>{row.plan}</td>
                                    <td><Badge bg={statusVariant(row.status)}>{row.status}</Badge></td>
                                    <td>{row.note}</td>
                                </tr>
                            )) : (
                                <tr>
                                    <td colSpan={6} className="text-center text-muted py-2">Load users to start.</td>
                                </tr>
                            )}
                            </tbody>
                        </Table>
                    </div>
                </Modal.Body>
                <Modal.Footer className="py-2 px-3">
                    <Button variant="secondary" size="sm" disabled={running} onClick={handleClose}>Close</Button>
                </Modal.Footer>
            </Modal>
        </>
    );
};

export default AdminEmailMailerModal;
